/* eslint-disable react/prop-types */
/**
 * "Request from the map" on the document-services state picker.
 *
 * The state grid assumes the visitor can name their state, then their district
 * and survey number. This is the way through for the ones who can't: they know
 * the PLACE. They pin it here and ask for the document from that point, without
 * leaving /states for the Maps page.
 *
 * Two steps, one modal:
 *   1. pick — a map. One click drops the pin; we reverse-geocode it for the
 *      address and the district / mandal / village, and auto-select the state
 *      when the point falls in one we serve.
 *   2. form — the SAME RequestDocumentModal the Maps page uses, handed the
 *      pinned location. It owns the document catalogue, pricing and submission,
 *      so there is one request flow in the app, not two.
 *
 * Point-only by design: drawing a boundary is a Maps-page job, and a request
 * only ever stores the centre of one anyway.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { MapPin, X, Loader2 } from "lucide-react";
import LocationPickerMap from "../leaflet/LocationPickerMap";
import SurveyNumberOverlay from "../leaflet/SurveyNumberOverlay";
import {
  reverseGeocode,
  inferLandType,
  parseBoundingBox,
  geocodeAddress,
  locatePlace,
} from "../../utils/geocoding";
import { getKnownLocation } from "../../utils/geolocation";
import { fetchCrowdForm } from "../../../pages/maps/crowdDataApi";
import MapRequestCard from "./MapRequestCard";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { loginPopupHandler } from "../../../redux/features/auth/loginSlice";
import { useSurveyLayerOn } from "../../utils/surveyLayerPref";

// The land-record hierarchy from a reverse-geocode result, in the shape the
// backend stores. Only the parts that actually resolved are kept (OSM carries a
// different subset per point); null when none did, so a request never ships an
// empty object. Mirrors the same helper on the Maps page.
function pickLocationDetails(addr) {
  if (!addr) return null;
  const out = {};
  ["district", "mandal", "village", "city", "pincode"].forEach((k) => {
    const v = addr[k];
    if (v && String(v).trim()) out[k] = String(v).trim();
  });
  return Object.keys(out).length > 0 ? out : null;
}

// The app's default frame when we know nothing about where the visitor means.
const HYDERABAD = { lat: 17.385, lng: 78.4867 };

// Geocoded state frames, cached for the session. A state's extent never changes,
// and reopening the map shouldn't re-ask Nominatim (which asks callers to keep
// request volume low).
const stateFrameCache = new Map();

/**
 * Centre + zoom that frames a bounding box in a viewport of `viewportPx` wide.
 * Web-Mercator tiles are 256px and span 360° at zoom 0, so the zoom that fits
 * `span` degrees across `viewportPx` is log2(360 · px / (256 · span)).
 * @param {{south:number,north:number,west:number,east:number}} b
 * @param {number} viewportPx
 * @returns {{center:{lat:number,lng:number}, zoom:number}}
 */
function frameForBBox(b, viewportPx) {
  const span = Math.max(Math.abs(b.north - b.south), Math.abs(b.east - b.west)) || 1;
  const z = Math.log2((360 * viewportPx) / (256 * span));
  return {
    center: { lat: (b.north + b.south) / 2, lng: (b.east + b.west) / 2 },
    // Floor already leaves margin — at the fitted zoom the viewport shows ~35%
    // more than the state's own span — so a further step out just framed the
    // neighbours and the coastline instead of the state.
    zoom: Math.max(4, Math.min(12, Math.floor(z))),
  };
}

export default function MapRequestModal({
  open,
  onClose,
  // Context the HOST page already knows, so the map is asked only for what it
  // alone can answer — the location. Opened from a document page
  // (/<state>/<document>) all three arrive filled and the form has nothing left
  // to ask but the delivery option; opened from the state picker all three are
  // absent and the map/visitor supply them.
  fixedState = null, // { key, name }
  fixedMode = null, // "agriculture" | "non-agriculture"
  fixedDocument = null, // { id, name }
  // A location the HOST already knows (e.g. the property this document page was
  // opened for), as { lat, lng }. When present the map opens framed on it
  // instead of asking for a fix — the visitor came here about THAT place.
  initialCenter = null,
  // The place the host's form already names, as { village, mandal, district,
  // city, pincode } (any may be empty). When there is no `initialCenter` the
  // map opens on this place rather than on the whole state.
  initialPlace = null,
}) {
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null); // { fields: [STATE, MODE] }
  const [position, setPosition] = useState(null); // { lat, lng }
  // Survey-number parcels on the request map: the user's remembered choice.
  const [surveyOn, toggleSurvey] = useSurveyLayerOn();
  const [address, setAddress] = useState("");
  const [locationDetails, setLocationDetails] = useState(null);
  const [stateOpt, setStateOpt] = useState(null); // { key, name } from config
  // The state the geocoder named, even when we serve no catalogue for it. A pin
  // in, say, Kerala resolves nothing in `stateOpt` (the crowd config lists only
  // the served states), and without this the request would have no state at all
  // — which the backend rejects. Kept so a location-only request still says
  // WHERE it is.
  const [geoState, setGeoState] = useState(null); // { key, name }
  // "agriculture" | "non-agriculture", inferred from the pinned point. Null when
  // the point doesn't say, in which case the form asks rather than guessing.
  const [landType, setLandType] = useState(null);
  // The extent of the place the pin sits in, outlined on the map so the pin is
  // visibly IN that area. From the search result when one was picked, else from
  // the reverse-geocode of the point.
  const [areaBox, setAreaBox] = useState(null);
  const [resolving, setResolving] = useState(false);
  // Centre + zoom that frames the state this page is about, once geocoded.
  const [stateFrame, setStateFrame] = useState(null);
  // A closer opening view than the state: the place the host's form names, or
  // the visitor's own location when we already have it. { center, zoom }.
  const [nearFrame, setNearFrame] = useState(null);
  // The map's opening view is fixed at mount (Leaflet boots uncontrolled), so a
  // frame that arrives later can't move it — and FlyTo only ever zooms IN, so it
  // can't pull back to a whole state either. Hence: hold the map back for the
  // one geocode, then mount it already framed. False only while that is in
  // flight; true immediately when there is nothing to wait for.
  const [frameReady, setFrameReady] = useState(true);

  // The crowd form config (which states / property types are served). Static and
  // auth-exempt, so it loads for logged-out visitors too — they can pin and see
  // the place named before being asked to log in.
  useEffect(() => {
    if (!open || config) return;
    let alive = true;
    fetchCrowdForm()
      .then((cfg) => alive && setConfig(cfg))
      .catch(() => alive && setConfig(null));
    return () => {
      alive = false;
    };
  }, [open, config]);

  // Where the map opens when the host gave no exact point, most specific first:
  //   1. the place the host's form already names (village → mandal → district);
  //   2. the visitor's location, but only when we can have it without a prompt
  //      (already located this session, or permission already granted) and,
  //      on a state page, only when it lies inside that state;
  //   3. the state the host page is about (e.g. /telangana).
  // The map is held back until this settles (see frameReady); every step is
  // best-effort and a miss just falls through to the next.
  const placeKey = JSON.stringify(initialPlace || {});
  useEffect(() => {
    if (!open) return;
    if (initialCenter) {
      setFrameReady(true);
      return;
    }
    let alive = true;
    setFrameReady(false);
    setNearFrame(null);
    (async () => {
      const place = JSON.parse(placeKey);
      const hasPlace = ["village", "mandal", "district", "city", "pincode"].some(
        (k) => String(place[k] || "").trim()
      );
      if (hasPlace) {
        const hit = await locatePlace({ ...place, state: fixedState?.name });
        if (hit) {
          if (alive) setNearFrame({ center: { lat: hit.lat, lng: hit.lng }, zoom: hit.zoom });
          return;
        }
      }

      const name = fixedState?.name;
      let frame = null;
      if (name) {
        const key = name.toLowerCase();
        frame = stateFrameCache.get(key) || null;
        if (!frame) {
          // One geocode of the state name gives its extent directly (a search
          // suggestion would need a second call to resolve).
          const stateHit = await geocodeAddress(name);
          const bbox = stateHit?.bbox || null;
          if (bbox) {
            frame = {
              ...frameForBBox(
                bbox,
                Math.min(typeof window !== "undefined" ? window.innerWidth : 900, 1024)
              ),
              bbox,
            };
            stateFrameCache.set(key, frame);
          }
        }
        if (alive) setStateFrame(frame);
      }

      const me = await getKnownLocation();
      const b = frame?.bbox;
      const inState =
        !name ||
        (b && me && me.lat >= b.south && me.lat <= b.north && me.lng >= b.west && me.lng <= b.east);
      if (me && inState && alive) setNearFrame({ center: me, zoom: 15 });
    })()
      .catch(() => {})
      .finally(() => alive && setFrameReady(true));
    return () => {
      alive = false;
    };
    // Keyed on VALUES, not object identity: hosts pass `fixedState` /
    // `initialCenter` as fresh objects on every render (the homepage hero
    // re-renders every 3 s for its rotating placeholder), and re-running this
    // would hide the map, rebuild it and repeat the framing lookups each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedState?.key, fixedState?.name, initialCenter?.lat, initialCenter?.lng, placeKey]);

  // Close on Escape — but only when the request form isn't up, since that has
  // its own dismissal and should not take the map down with it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // The page behind must not scroll while a full-screen map is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Where the map opens, in order of what we actually know:
  //   1. a location the host handed us — the property in question;
  //   2. the form's place, or a location we already have (nearFrame, above);
  //   3. the host page's state — then the visitor's own location (asked for
  //      on open, once per visit) takes over if it lies inside that state;
  //   4. otherwise the visitor's own location, or Hyderabad, the app's
  //      default frame, if they decline.
  // (1) and (2) never prompt: asking for a fix and then ignoring it because we
  // already had a better location would be a pointless permission prompt.
  const hasInitial =
    !!initialCenter &&
    Number.isFinite(initialCenter.lat) &&
    Number.isFinite(initialCenter.lng);
  const openCenter = hasInitial
    ? initialCenter
    : nearFrame?.center || stateFrame?.center || HYDERABAD;

  if (!open) return null;

  // Rendered into <body>, not in place. A full-screen overlay is at the mercy
  // of whatever its host page does to its subtree — the homepage forces
  // `position: relative; z-index: 1` onto every direct child of <main>
  // (HomePage.theme.css `.jaaga-home > *`) to stack content over a texture
  // layer, which silently demoted this from `fixed inset-0` to an inline
  // 105px block. Portalling makes the modal immune to that on every page
  // rather than leaving each host page to remember.
  if (typeof document === "undefined") return null; // prerender pass

  // Name the pinned point: address + revenue hierarchy, and auto-select the
  // state when it falls in one we serve (the request form still lets the user
  // change it, so a geocoder miss is never a dead end).
  const resolvePoint = async (lat, lng) => {
    setResolving(true);
    try {
      // `admin`: the request needs district / mandal / village, which the
      // backend fills from OpenStreetMap where Google has none.
      const addr = await reverseGeocode(lat, lng, { admin: true });
      setAddress(addr?.formatted || "");
      setLocationDetails(pickLocationDetails(addr));
      // Only fall back to the reverse-geocoded extent when a search hasn't
      // already given us a better one for this same pin.
      setAreaBox((cur) => cur || addr?.bbox || null);
      // Ask the map what kind of land this is. Null when it can't tell, which is
      // common — the caller falls back to the configured default rather than
      // putting the question to the visitor.
      setLandType(inferLandType(addr));
      const opts = config?.fields?.find((f) => f.id === "STATE")?.options || [];
      const target = (addr?.state || "").trim().toLowerCase();
      const match = target
        ? opts.find(
            (o) =>
              o.name?.toLowerCase() === target || o.key?.toLowerCase() === target
          )
        : null;
      setStateOpt(match || null);
      setGeoState(
        addr?.state
          ? {
              key: String(addr.state).trim().toLowerCase().replace(/\s+/g, "-"),
              name: String(addr.state).trim(),
            }
          : null
      );
    } finally {
      setResolving(false);
    }
  };

  const onPick = (lat, lng) => {
    setPosition({ lat, lng });
    setAddress("");
    setLocationDetails(null);
    setStateOpt(null);
    setGeoState(null);
    setLandType(null);
    setAreaBox(null);
    resolvePoint(lat, lng);
  };

  const clearPin = () => {
    setPosition(null);
    setAddress("");
    setLocationDetails(null);
    setStateOpt(null);
    setGeoState(null);
    setLandType(null);
    setAreaBox(null);
  };

  // Requesting needs a session. Gated before the document step rather than at
  // submit, so nobody picks a document and a price only to be stopped at the end.
  const requireLogin = () => {
    if (isLoggedIn()) return true;
    dispatch(loginPopupHandler(true));
    return false;
  };

  const closeAll = () => {
    clearPin();
    onClose?.();
  };

  // The documents this short flow offers, straight from the backend config
  // (public/crowd-data/index.json → mapRequest), so the list is editable without
  // touching the UI. Empty when the config failed to load, which falls back to
  // the full catalogue rather than to nothing. Irrelevant when the host page
  // already named the document.
  const allowedDocuments = Array.isArray(config?.mapRequest?.documents)
    ? config.mapRequest.documents
    : [];

  // What the form will actually be opened with. The host page's context wins
  // over the map's: someone on the Telangana EC page came for a Telangana EC,
  // and the pin is them saying WHERE — not changing what they asked for.
  const effectiveState = fixedState || stateOpt || geoState;
  const effectiveMode = fixedMode || landType;

  // ...which makes a pin in a different state worth saying out loud. It doesn't
  // block — the page's state still wins — but silently sourcing a Telangana
  // record for a pin in Andhra Pradesh would be a bad surprise.
  const stateMismatch =
    !!fixedState &&
    !!stateOpt &&
    stateOpt.key !== fixedState.key;

  // Village · Mandal · District, smallest unit first, the way a land record
  // reads. Empty when the geocoder resolved none of them.
  const area = [
    locationDetails?.village,
    locationDetails?.mandal,
    locationDetails?.district,
  ]
    .filter(Boolean)
    .join(" · ");

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1150] flex flex-col bg-[#153568]/50 backdrop-blur-sm sm:p-4">
        <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white sm:mx-auto sm:max-w-5xl sm:rounded-2xl sm:shadow-[0_30px_70px_-20px_rgba(21,53,104,0.6)] sm:ring-1 sm:ring-black/5">
          {/* Header — the app's deep-navy premium surface with a gold accent
              tile, so the dialog reads as part of the product rather than a
              plain browser modal. The step line matters: it tells the visitor
              the map is the FIRST of two steps, so nobody wonders where they
              get to choose the document. */}
          <div className="relative flex shrink-0 items-center gap-3 overflow-hidden bg-gradient-to-r from-[#002b5c] to-primary px-4 py-3.5 sm:gap-3.5 sm:px-5">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -left-10 -top-16 h-44 w-44 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(28,75,147,0.22) 0%, transparent 70%)",
              }}
            />
            <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-gold to-orange text-white shadow-[0_6px_18px_-6px_rgba(255,169,0,0.9)] sm:h-10 sm:w-10">
              <MapPin className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
            </span>
            <div className="relative min-w-0 flex-1">
              <h2 className="truncate text-[15px] font-bold leading-tight text-white sm:text-base">
                Request document from map
              </h2>
              <p className="mt-1 truncate text-[11px] font-semibold text-white/70">
                {position
                  ? "Drag the pin to adjust, or pick your document"
                  : "Point at the property — no state or survey number needed"}
              </p>
            </div>
            <button
              type="button"
              onClick={closeAll}
              aria-label="Close"
              className="relative grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Map */}
          <div className="relative min-h-0 flex-1">
            {!frameReady && (
              <div className="absolute inset-0 z-[5] grid place-items-center bg-[#f2f6fb]">
                <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-grey">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {fixedState?.name ? `Framing ${fixedState.name}…` : "Finding your area…"}
                </span>
              </div>
            )}
            {frameReady && (
            <LocationPickerMap
              className="h-full w-full"
              style={{ height: "100%", width: "100%" }}
              position={position}
              onPick={onPick}
              // Same precision gate the Maps page uses: a click while zoomed out
              // frames the spot first, the next one pins it.
              minPickZoom={16}
              pickZoom={17.75}
              zoomToClick
              zoom={
                position
                  ? 16
                  : hasInitial
                    ? 16
                    : nearFrame
                      ? nearFrame.zoom
                      : stateFrame
                        ? stateFrame.zoom
                        : 9
              }
              defaultCenter={openCenter}
              // Unless the host gave us the property or the form's place, open
              // on the visitor's own location (asked once per visit, reused
              // after). Centres only; it never pins, so nothing is selected on
              // their behalf just by opening the map. On a state page it only
              // moves there when they are inside that state — otherwise the
              // state frame is the more useful view.
              autoLocate={!hasInitial && !nearFrame}
              autoLocateRecenter={(fix) => {
                const b = stateFrame?.bbox;
                if (!fixedState || !b || !fix) return true;
                return (
                  fix.lat >= b.south && fix.lat <= b.north &&
                  fix.lng >= b.west && fix.lng <= b.east
                );
              }}
              defaultBase="Satellite"
              baseToggle
              zoomBottomRight
              // Pressing "my location" here means "request for where I am", so
              // the fix pins that spot rather than only panning to it.
              pickOnLocate
              // First click places the point (and flies in). Without this the
              // map opens zoomed out to the whole state and a click only zooms,
              // so nothing appears where you clicked.
              pickAtAnyZoom
              // Name the pin on the map and outline the area it sits in, so the
              // map itself says where this is — not just the footer.
              positionLabel={area || locationDetails?.city || ""}
              area={areaBox}
              // On-map search: this modal has no toolbar of its own to drive it.
              showSearch
              onSearchSelect={({ lat, lng, boundingbox } = {}) => {
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                onPick(lat, lng);
                // Set AFTER onPick (which clears it): the searched place's own
                // extent describes the area better than whatever the point
                // reverse-geocodes to.
                setAreaBox(parseBoundingBox(boundingbox));
              }}
              autoLocateZoom={16}
              extraOverlays={[{ key: "survey", name: "Survey Numbers" }]}
              activeExtras={surveyOn ? ["survey"] : []}
              onToggleExtra={toggleSurvey}
            >
              {/* Display-only here: a tap on this map places the request pin. */}
              {surveyOn && <SurveyNumberOverlay showSearch={false} />}
            </LocationPickerMap>
            )}
          </div>

          {/* The pinned property's card, floated over the map (Landeed-style)
              rather than a footer bar: it sits next to the pin it describes, and
              it owns the whole request — address, document, price, submit. When
              nothing is pinned yet the map is left clear but for one hint. */}
          {position ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1000] flex justify-center px-3 pb-3 sm:justify-end sm:px-5 sm:pb-5">
              {resolving ? (
                <div className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[12px] font-semibold text-grey shadow-[0_24px_60px_-18px_rgba(21,53,104,0.55)]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Finding this place…
                </div>
              ) : (
                <MapRequestCard
                  position={position}
                  address={address}
                  locationDetails={locationDetails}
                  areaLabel={area}
                  state={effectiveState}
                  // False when the pin is outside the states we serve a catalogue
                  // for — the card then offers a location-only request.
                  stateServed={!!(fixedState || stateOpt)}
                  mode={effectiveMode}
                  fixedDocument={fixedDocument}
                  allowedDocuments={fixedDocument ? [] : allowedDocuments}
                  onClose={closeAll}
                  onRepin={clearPin}
                  requireLogin={requireLogin}
                  stateMismatch={stateMismatch ? stateOpt?.name : null}
                />
              )}
            </div>
          ) : (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1000] flex justify-center px-3 pb-16 max-sm:pr-14 sm:pb-10">
              {/* Above the bottom-left Google logo + credits row (and, on
                  phones, the scale bar under it), which Google's terms require
                  to stay visible. */}
              <p className="rounded-full bg-white/95 px-4 py-2 text-[12px] font-medium text-grey shadow-[0_10px_30px_-12px_rgba(21,53,104,0.5)] backdrop-blur-sm">
                No state or survey number needed —{" "}
                <span className="font-semibold text-primary">
                  just point at the property
                </span>
              </p>
            </div>
          )}

        </div>
      </div>

    </>,
    document.body
  );
}
